import { BASE_URL } from '@avanta/shared/constants/common';
import { service, type ServiceRequestConfig } from '@avanta/shared/services/api';
import { AppointmentService as SharedAppointmentService } from '@avanta/shared/services/appointments';
import { type GetAppointmentsParams, type Treatment } from '@avanta/shared/types/appointments';
import { type AxiosResponse } from 'axios';
import {
  type Appointment,
  type AppointmentAvailability,
  type CancelAppointmentPayload,
  type Center,
  type GetEnterpriseAppointmentsCountParams,
  type GetReportNotificationsParams,
  type GetEnterpriseAppointmentsParams,
  type GetPsychologistAvailabilityParams,
  type ReportNotificationAppointment,
  type ReportNotificationsCountResponse,
  type SessionDetails,
  type UploadDocumentPayload,
} from 'types/appointments';
import { toDocumentFormData } from 'utils/storage';

const API_URL = `${BASE_URL}appointments`;

export type AppointmentServiceTypes = {
  getAppointments: (
    patientId: string,
    params?: GetAppointmentsParams
  ) => Promise<AxiosResponse<Appointment[]>>;
  cancel: ({
    psychologistId,
    appointmentId,
    patientId,
  }: CancelAppointmentPayload) => Promise<AxiosResponse<void>>;
  getSessionDetails: (sessionId: string) => Promise<AxiosResponse<SessionDetails>>;
  getCenterDetails: (centerId: number) => Promise<AxiosResponse<Center>>;
  getPsychologistAvailability: (
    params: GetPsychologistAvailabilityParams
  ) => Promise<AxiosResponse<AppointmentAvailability[]>>;
  getAppointmentsHistory: (patientId: string) => Promise<AxiosResponse<Appointment[]>>;
  getEnterpriseAppointments: (
    enterpriseId: string,
    params?: GetEnterpriseAppointmentsParams
  ) => Promise<AxiosResponse<Appointment[]>>;
  getEnterpriseAppointmentsCount: (
    enterpriseId: string,
    params?: GetEnterpriseAppointmentsCountParams
  ) => Promise<AxiosResponse<{ count: number }>>;
  getReportNotifications: (
    enterpriseId: string,
    params?: GetReportNotificationsParams
  ) => Promise<AxiosResponse<ReportNotificationAppointment[]>>;
  getReportNotificationsCount: (
    enterpriseId: string,
    params?: Pick<GetReportNotificationsParams, 'verified'>
  ) => Promise<AxiosResponse<ReportNotificationsCountResponse>>;
  verifyReportNotification: (appointmentId: string) => Promise<AxiosResponse<Appointment>>;
  getTreatments: (params: {
    psychologistId?: string;
    contractId?: string;
    patientId?: string;
  }) => Promise<AxiosResponse<Treatment[]>>;
  uploadDocument: (payload: UploadDocumentPayload) => Promise<void>;
  uploadLocalDocument: (payload: UploadDocumentPayload) => Promise<void>;
  getAppointmentByPatient: ({
    appointmentId,
    patientId,
  }: {
    appointmentId: string;
    patientId: string;
  }) => Promise<AxiosResponse<Appointment>>;
};

export const AppointmentService: AppointmentServiceTypes = {
  getAppointments: SharedAppointmentService.getAppointments,
  cancel: SharedAppointmentService.cancel,
  getSessionDetails: SharedAppointmentService.getSessionDetails,
  getCenterDetails: SharedAppointmentService.getCenterDetails,
  getPsychologistAvailability: async params => service.get(`${API_URL}/availability`, { params }),
  getAppointmentsHistory: async patientId =>
    service.get(`${API_URL}/patients/${patientId}/confirmed`),
  getEnterpriseAppointments: async (enterpriseId, params) =>
    service.get(`${API_URL}/enterprises/${enterpriseId}`, { params }),
  getEnterpriseAppointmentsCount: async (enterpriseId, params) =>
    service.get(`${API_URL}/enterprises/${enterpriseId}/count`, { params }),
  getReportNotifications: async (enterpriseId, params) =>
    service.get(`${API_URL}/enterprises/${enterpriseId}/report-notifications`, { params }),
  getReportNotificationsCount: async (enterpriseId, params) =>
    service.get(`${API_URL}/enterprises/${enterpriseId}/report-notifications/count`, { params }),
  verifyReportNotification: async appointmentId => {
    const config: ServiceRequestConfig = { skipGlobalErrorHandler: true };
    return service.post(`${API_URL}/${appointmentId}/report-verification`, undefined, config);
  },
  getTreatments: async params =>
    service.get(`${API_URL}/insurance/treatments`, {
      params,
    }),
  uploadDocument: async ({ appointmentId, document }) => {
    await service.post(`${API_URL}/${appointmentId}/documents`, toDocumentFormData(document), {
      headers: { 'Content-Type': false },
    });
  },
  uploadLocalDocument: async ({ appointmentId, document }) => {
    await service.post(
      `${API_URL}/${appointmentId}/document-uploads`,
      toDocumentFormData(document),
      {
        headers: { 'Content-Type': false, 'Idempotency-Key': crypto.randomUUID() },
      }
    );
  },
  getAppointmentByPatient: async ({ appointmentId, patientId }) =>
    service.get(`${API_URL}/${appointmentId}/patients/${patientId}`),
};
